/* =============================================================================
   VIEWS / ADMIN / ADMIN-ORDER-ENTRY-STATE — Modul-State + Pure-Helpers
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5 Cut 1/4
   ----------------------------------------------------------------------------
   Shared State + Pure-Helpers für admin-order-entry-Modul. _state als ESM
   live binding exportiert — Sub-Module (delivery, dinein, submit) mutieren
   Properties direkt. Pattern S69 menu-picker (bewährt).
============================================================================ */

// — Modul-State (nur während das Modal offen ist) —————————————
// S60 — 3-Pfad-Architektur:
//   step 1 = Service-Auswahl (Hier/Takeaway/Lieferung)
//   step 2 = (Hier) Tisch + Items + Verzweigung „Jetzt bestellen vs. Erst Platz nehmen"
//            (Takeaway) Items direkt
//            (Lieferung) Adresse + Items
export const _state = {
  step: 1,
  serviceType: null,        // 'takeaway' | 'dine-in' | 'delivery'
  tableId: null,
  assignedTableIds: [],     // S134 P2 — Mehr-Tisch-Walk-In aus dem Assignment-Picker
  notes: '',
  // Delivery-spezifisch (S60 Block D):
  deliveryAddress: {        // wird in Step 2 (delivery) befüllt
    name: '', phone: '', email: '',
    street: '', plz: '', city: '', floor: '', notes: ''
  },
  deliveryZone: null,       // {zoneId, feeCents, minOrderCents} nach check_delivery_zone-RPC
  deliveryCustomerId: null, // S60 W4.7 — gesetzt nach Telefon-Lookup im Liefer-Pfad
  // S60 Block W1 — Hier-Essen-Daten (step 2 für dine-in, dann optional als Walk-In submit)
  seatOnly: false,
  dineInForm: {
    guests:      2,
    guestName:   '',
    guestPhone:  '',
    durationMin: 90,
    notes:       '',
    customerId:  null      // wird gesetzt wenn Telefon-Lookup einen Treffer findet
  },
  reservationContext: null, // S61 — Reservation-Context aus dem Confirm-Modal
  // REF-7b (S75) — Cockpit-Mail-Opt-In (service-type-übergreifend):
  customerEmail: '',        // E-Mail für Auto-Mails (alle 3 Pfade)
  mailOptIn: {              // welche Auto-Mails der Operator freigegeben hat
    confirmed: false, ready: false, cancelled: false, receipt: false,
    _touched: false         // true sobald Operator eine Checkbox selbst setzt
  }
};

export function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { tables: [], menu: [], settings: {} };
}

// — Steuersatz-Helper (analog takeaway-form.js) ————————————————
export function deriveTaxRate(menuItem, settings) {
  const food   = Number((settings && settings.taxRateFood)   || 7);
  const drinks = Number((settings && settings.taxRateDrinks) || 19);
  const itemType = (menuItem && menuItem.itemType) || 'main';
  return itemType === 'drink' ? drinks : food;
}

export function lineToRpcItem(line, menuList, settings) {
  const menu = (menuList || []).find(m => m.id === line.ref) || null;
  const qty  = Math.max(1, Number(line.qty) || 1);
  const unitPriceCents = Math.round((Number(line.lineTotal) || 0) / qty * 100);
  return {
    menuItemId: line.ref,
    name:       line.name,
    category:   line.category,
    qty,
    unitPriceCents,
    taxRate:    deriveTaxRate(menu, settings),
    customizations: {
      bun:                 line.bun || null,
      patty:               line.patty || null,
      dressing:            line.dressing || null,
      milk:                line.milk || null,
      removedIngredients:  line.removedIngredients || [],
      extras:              line.extras || [],
      sauces:              line.sauces || [],
      // S229 T1 — Mehrfach-Beilagen als sides[]; Leser tolerant für Legacy-side.
      sides:               line.sides || [],
      sideSauces:          line.sideSauces || []
    },
    // S196 (Jonas) — Positions-Kommentar (Cockpit) → order_items.notes. Leerer
    // Kommentar bleibt NULL. create_admin_order + add_items_to_order persistieren
    // notes; KDS/Theke zeigen sie ab der ersten Runde.
    notes: (line.note && String(line.note).trim()) || null
  };
}

/**
 * S197 Cut b2 — RÜCKWÄRTS-Mapper: eine gespeicherte Position (order_item, gemappt
 * via mapOrderItemRow ODER eine RPC-Item-Form) → Konfigurator-Line, INVERS zu
 * lineToRpcItem. Für den „Position bearbeiten"-Re-Edit im Tisch-Warenkorb.
 *
 * - Deal-Positionen (`customizations._dealMeta`) sind NICHT re-editierbar → null.
 * - Menü-Item gelöscht/deaktiviert (ref nicht im aktuellen Menü) → null.
 * - `unitPrice` = AKTUELLER Menü-Basispreis (recalcLine addiert die gespeicherten
 *   Options-Preise aus customizations wieder drauf → „Gast bestellt neu"-Preis).
 * - Feld-tolerant: liest sowohl DB-Shape (nameSnapshot/categorySnapshot) als auch
 *   RPC-Item-Shape (name/category) → ermöglicht den Roundtrip-Test.
 *
 * @returns {object|null} Draft-Line (lineId '__draft__') oder null (nicht editierbar)
 */
export function orderItemToLine(item, menuList) {
  if (!item) return null;
  const cust = (item.customizations && typeof item.customizations === 'object') ? item.customizations : {};
  if (cust._dealMeta) return null; // Deal-Position — eigener Cut, hier geblockt
  const ref = item.menuItemId || item.menu_item_id || null;
  if (!ref) return null;
  const menu = (menuList || []).find(m => m.id === ref) || null;
  if (!menu) return null; // Menü-Item inzwischen weg → nicht editierbar
  const arr = (v) => Array.isArray(v) ? v.slice() : [];
  return {
    lineId: '__draft__',
    ref,
    name:      item.nameSnapshot || item.name_snapshot || item.name || menu.name || '',
    category:  item.categorySnapshot || item.category_snapshot || item.category || menu.category || '',
    qty:       Math.max(1, Number(item.qty) || 1),
    unitPrice: Number(menu.price) || 0,
    bun:      cust.bun      || null,
    patty:    cust.patty    || null,
    dressing: cust.dressing || null,
    milk:     cust.milk     || null,
    removedIngredients: arr(cust.removedIngredients),
    extras:             arr(cust.extras),
    sauces:             arr(cust.sauces),
    // S229 T1 — Restore: neue sides[] bevorzugt, Legacy-side (Objekt) migrieren.
    sides:    arr(cust.sides).length ? arr(cust.sides) : (cust.side ? [cust.side] : []),
    sideSauces:         arr(cust.sideSauces),
    note:     item.notes != null ? (item.notes || '') : (item.note || '')
  };
}
